// cSpell:ignore workstream
import { http, HttpResponse, delay } from 'msw'
import { mockSentryIssues } from './sentry/sentryIssues.mock'
import { mockPriorityCounts } from './sentry/priorityCounts.mock'
import { mockSeverityPercentage } from './sentry/severityPercentage.mock'
import { mockTrendGraph } from './sentry/trendGraph.mock'
import { mockIssueDetails } from './sentry/issueDetails.mock'
import { mockExportIssues } from './sentry/exportIssues.mock'
import { mockSentryIssuesLinehaul, mockPriorityCountsLinehaul } from './sentry/sentryIssuesLinehaul.mock'
import { mockSentryIssuesBackoffice, mockPriorityCountsBackoffice } from './sentry/sentryIssuesBackoffice.mock'
import { mockSentryIssuesInbound, mockPriorityCountsInbound } from './sentry/sentryIssuesInbound.mock'
import { mockSentryIssuesOutbound, mockPriorityCountsOutbound } from './sentry/sentryIssuesOutbound.mock'
import { mockSentryIssuesDispatch, mockPriorityCountsDispatch } from './sentry/sentryIssuesDispatch.mock'
import {
  mockCloudWatchLogsLinehaul,
  mockCloudWatchLogsBackoffice,
  mockCloudWatchLogsInbound,
  mockCloudWatchLogsOutbound,
  mockCloudWatchLogsDispatchWeb,
  mockCloudWatchLogsDispatchMobile,
  mockCloudWatchLogsDockAutomation,
} from './cloudwatch/cloudWatchLogs.mock'
import { HandlerConfig } from '../devtools/types'

/**
 * MSW Request Handlers
 * Define all API mocks here
 * Following operations-iol-webapp patterns
 */
export const requestHandlers = (config: HandlerConfig) => [
  // Sentry Issues - Search endpoint
  http.get(`*/api/sentry/issues/search`, async ({ request }) => {
    await delay(config.delay || 500)

    const url = new URL(request.url)
    const query = url.searchParams.get('query') || ''
    const searchTerm = query.match(/title:"([^"]+)"/)?.[1] || ''

    // Extract culprit path from query parameter (format: culprit:"*/workstream/*")
    const culpritMatch = query.match(/culprit:"([^"]+)"/)?.[1] || ''

    // Determine which workstream data to use based on culprit path in query
    let workstreamData: typeof mockSentryIssues = mockSentryIssues
    if (culpritMatch.includes('linehaul')) {
      workstreamData = mockSentryIssuesLinehaul as typeof mockSentryIssues
    } else if (culpritMatch.includes('back-office')) {
      workstreamData = mockSentryIssuesBackoffice as typeof mockSentryIssues
    } else if (culpritMatch.includes('inbound')) {
      workstreamData = mockSentryIssuesInbound as typeof mockSentryIssues
    } else if (culpritMatch.includes('outbound')) {
      workstreamData = mockSentryIssuesOutbound as typeof mockSentryIssues
    } else if (culpritMatch.includes('dispatch')) {
      workstreamData = mockSentryIssuesDispatch as typeof mockSentryIssues
    }

    // Filter issues by search term if provided
    let filteredIssues = workstreamData.issues
    if (searchTerm) {
      filteredIssues = filteredIssues.filter((issue) => issue.title.toLowerCase().includes(searchTerm.toLowerCase()))
    }

    return HttpResponse.json({
      ...workstreamData,
      issues: filteredIssues,
      recordCount: filteredIssues.length,
    })
  }),

  // Sentry Issues - Regular endpoint
  http.get(`*/api/sentry/issues`, async ({ request }) => {
    await delay(config.delay || 500)

    const url = new URL(request.url)
    const query = url.searchParams.get('query') || ''

    // Extract culprit path from query parameter (format: culprit:"*/workstream/*")
    const culpritMatch = query.match(/culprit:"([^"]+)"/)?.[1] || ''

    // Determine which workstream data to use based on culprit path in query
    let workstreamData: typeof mockSentryIssues = mockSentryIssues
    if (culpritMatch.includes('linehaul')) {
      workstreamData = mockSentryIssuesLinehaul as typeof mockSentryIssues
    } else if (culpritMatch.includes('back-office')) {
      workstreamData = mockSentryIssuesBackoffice as typeof mockSentryIssues
    } else if (culpritMatch.includes('inbound')) {
      workstreamData = mockSentryIssuesInbound as typeof mockSentryIssues
    } else if (culpritMatch.includes('outbound')) {
      workstreamData = mockSentryIssuesOutbound as typeof mockSentryIssues
    } else if (culpritMatch.includes('dispatch')) {
      workstreamData = mockSentryIssuesDispatch as typeof mockSentryIssues
    }

    return HttpResponse.json(workstreamData)
  }),

  // Priority Counts
  http.get(`*/api/sentry/issues/priority-counts`, async ({ request }) => {
    await delay(config.delay || 300)

    const url = new URL(request.url)
    const query = url.searchParams.get('query') || ''

    // Extract culprit path from query parameter (format: culprit:"*/workstream/*")
    const culpritMatch = query.match(/culprit:"([^"]+)"/)?.[1] || ''

    // Determine which workstream priority counts to use based on culprit path in query
    let priorityCounts = mockPriorityCounts
    if (culpritMatch.includes('linehaul')) {
      priorityCounts = mockPriorityCountsLinehaul
    } else if (culpritMatch.includes('back-office')) {
      priorityCounts = mockPriorityCountsBackoffice
    } else if (culpritMatch.includes('inbound')) {
      priorityCounts = mockPriorityCountsInbound
    } else if (culpritMatch.includes('outbound')) {
      priorityCounts = mockPriorityCountsOutbound
    } else if (culpritMatch.includes('dispatch')) {
      priorityCounts = mockPriorityCountsDispatch
    }

    return HttpResponse.json(priorityCounts)
  }),

  // Severity Percentage
  http.get(`*/api/sentry/issues/severity-percentage`, async () => {
    await delay(config.delay || 300)
    return HttpResponse.json(mockSeverityPercentage)
  }),

  // Trend Graph
  http.get(`*/api/sentry/issues/trend-graph`, async () => {
    await delay(config.delay || 300)
    return HttpResponse.json(mockTrendGraph)
  }),

  // Issue Details
  http.get(`*/api/sentry/issues/:issueId/details`, async ({ params }) => {
    await delay(config.delay || 400)
    const { issueId } = params

    // Return mock details for any issue ID
    return HttpResponse.json({
      ...mockIssueDetails,
      issueId: issueId as string,
    })
  }),

  // Export Issues
  http.get(`*/api/sentry/issues/export`, async ({ request }) => {
    await delay(config.delay || 800)

    const url = new URL(request.url)
    const query = url.searchParams.get('query') || ''
    const searchTerm = query.match(/title:"([^"]+)"/)?.[1] || ''

    // Filter export data by search term if provided
    let filteredData = mockExportIssues.data
    if (searchTerm) {
      filteredData = filteredData.filter((issue) => issue.Issue.toLowerCase().includes(searchTerm.toLowerCase()))
    }

    return HttpResponse.json({
      ...mockExportIssues,
      data: filteredData,
      recordCount: filteredData.length,
    })
  }),

  // CloudWatch Logs API
  http.get(`*/logs/query`, async ({ request }) => {
    await delay(config.delay || 600)

    const url = new URL(request.url)
    const workstream = url.searchParams.get('workstream') || ''

    // Determine which workstream CloudWatch data to use
    let cloudWatchData = mockCloudWatchLogsLinehaul
    if (workstream === 'linehaul') {
      cloudWatchData = mockCloudWatchLogsLinehaul
    } else if (workstream === 'back-office') {
      cloudWatchData = mockCloudWatchLogsBackoffice
    } else if (workstream === 'inbound') {
      cloudWatchData = mockCloudWatchLogsInbound
    } else if (workstream === 'outbound') {
      cloudWatchData = mockCloudWatchLogsOutbound
    } else if (workstream === 'dispatch-web') {
      cloudWatchData = mockCloudWatchLogsDispatchWeb
    } else if (workstream === 'dispatch-mobile') {
      cloudWatchData = mockCloudWatchLogsDispatchMobile
    } else if (workstream === 'dock-automation') {
      cloudWatchData = mockCloudWatchLogsDockAutomation
    }

    return HttpResponse.json(cloudWatchData)
  }),
]
